import * as Yup from "yup";

import {dynamicTabConfig} from "@/components/layout/TabContentContainer.types.ts";
import {SLAReport, SLAReportSort, SLAResponseTimeSectionSort, StandardDateRange} from "@/lib/queries/reports.ts";
import {SLA_REPORT_STANDARD_DATE_RANGES, StandardDateRangeLabels} from "@/lib/types/reports.ts";
import {logoValidator} from "@/pages/reports/logos/SLAReportLogoDrawer.tsx";
import {SLAReportFormTabs, SLAReportFormValues} from "@/pages/reports/sla/SLAReportForm.types.ts";
import {mergeSchemas} from "@/pages/statuspages/statuspages-create/CreateStatusPageForm.utils.tsx";

export const groupInEditNameField = "_group_in_edit_name";
export const groupInEditChecksField = "_group_in_edit_checks";
export const newGroupsField = "new_groups";
export const deleteGroupsField = "delete_groups";
export const checksField = "services_selected";

export const defaultSLAReportFormConfig: Record<SLAReportFormTabs, dynamicTabConfig> = {
  global: {
    isVisible: true,
  },
  reportingGroups: {
    isVisible: true,
  },
};

export const SLAReportSettingsSectionSchema = Yup.object().shape(
  {
    id: Yup.number().optional(),
    name: Yup.string().required("This field cannot be blank.").max(100),
    default_date_range: Yup.mixed<StandardDateRange>()
      .oneOf(Object.values(StandardDateRange))
      .required("This field is required."),
    [checksField]: Yup.array().of(Yup.number().required()).required(),
    services_tags: Yup.array().of(Yup.number().required()).required(),
    show_uptime_section: Yup.boolean()
      .default(true)
      .when("show_response_time_section", {
        is: false,
        then: (schema) => schema.oneOf([true], "At least one section required"),
      }),
    show_uptime_sla: Yup.boolean().default(true).required(),
    filter_with_downtime: Yup.boolean().default(false).required(),
    filter_uptime_sla_violations: Yup.boolean().default(false).required(),
    uptime_section_sort: Yup.mixed<SLAReportSort>()
      .oneOf(Object.values(SLAReportSort))
      .required("This field is required."),
    show_response_time_section: Yup.boolean()
      .default(false)
      .when("show_uptime_section", {
        is: false,
        then: (schema) => schema.oneOf([true], "At least one section required"),
      }),
    show_response_time_sla: Yup.boolean().default(true).required(),
    filter_slowest: Yup.boolean().default(false).required(),
    filter_response_time_sla_violations: Yup.boolean().default(false).required(),
    response_time_section_sort: Yup.mixed<SLAResponseTimeSectionSort>()
      .oneOf(Object.values(SLAResponseTimeSectionSort))
      .required("This field is required."),
    logo: logoValidator,
  },
  [
    ["show_uptime_section", "show_response_time_section"],
    ["show_response_time_section", "show_uptime_section"],
  ]
);

export const SLAReportingGroupsSectionSchema = Yup.object().shape({
  [newGroupsField]: Yup.array()
    .of(
      Yup.object({
        name: Yup.string().required("This field cannot be blank."),
        group_services: Yup.array().of(Yup.number().required()).required(),
      })
    )
    .required(),
  [deleteGroupsField]: Yup.array().of(Yup.number().required()).required(),
});

export const SLAReportMultiServiceSelectSchema = Yup.object().shape({
  [groupInEditNameField]: Yup.string(),
  [groupInEditChecksField]: Yup.array().of(Yup.number()),
});

export const SLAReportFormValuesSchema = mergeSchemas<SLAReportFormValues>(
  SLAReportSettingsSectionSchema,
  SLAReportingGroupsSectionSchema,
  SLAReportMultiServiceSelectSchema
);

export const AVAILABLE_DATE_RANGES = SLA_REPORT_STANDARD_DATE_RANGES.map((range) => ({
  label: StandardDateRangeLabels[range],
  value: range,
}));

export const getSLAReportFormDefaultValues = (slaReport?: SLAReport) => {
  return {
    id: slaReport?.id,
    name: slaReport?.name || "",
    default_date_range: slaReport?.default_date_range || StandardDateRange.THIS_MONTH,
    [checksField]: slaReport?.services_selected?.map((s) => s.id) || [],
    services_tags: slaReport?.tags.map((t) => t.id) || [],
    show_uptime_section: slaReport ? slaReport.show_uptime_section : true,
    show_uptime_sla: slaReport ? slaReport.show_uptime_sla : true,
    filter_with_downtime: slaReport ? slaReport.filter_with_downtime : false,
    filter_uptime_sla_violations: slaReport ? slaReport.filter_uptime_sla_violations : false,
    uptime_section_sort: slaReport ? slaReport.uptime_section_sort : SLAReportSort.BY_UPTIME,
    show_response_time_section: slaReport ? slaReport.show_response_time_section : false,
    show_response_time_sla: slaReport ? slaReport.show_response_time_sla : true,
    filter_slowest: slaReport ? slaReport.filter_slowest : false,
    filter_response_time_sla_violations: slaReport ? slaReport.filter_response_time_sla_violations : false,
    response_time_section_sort: slaReport
      ? slaReport.response_time_section_sort
      : SLAResponseTimeSectionSort.BY_RESPONSE_TIME,
    [newGroupsField]: [],
    [deleteGroupsField]: [],
    logo: slaReport?.logo_url,
    [groupInEditNameField]: "",
    [groupInEditChecksField]: [],
  };
};
